import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import BrandLogo from "../components/BrandLogo";

const BRAND = "Zaya Life";
const TAGLINE = "Abu Dhabi Surrogacy Agency";

function useInView(threshold = 0.12) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setInView(true); obs.unobserve(el); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

function FadeIn({ children, delay = 0, style = {} }) {
  const [ref, inView] = useInView(0.08);
  return (
    <div ref={ref} style={{
      opacity: inView ? 1 : 0,
      transform: inView ? "translateY(0)" : "translateY(28px)",
      transition: `opacity 0.7s cubic-bezier(0.22,1,0.36,1) ${delay}s, transform 0.7s cubic-bezier(0.22,1,0.36,1) ${delay}s`,
      ...style,
    }}>{children}</div>
  );
}

const C = {
  bg: "#FDFBF7", bgWarm: "#F8F4ED", bgSoft: "#F2EDE4",
  text: "#2C2520", textMed: "#5C5347", textLight: "#8C8278", textFaint: "#B0A89E",
  sage: "#7A9B6D", sageDark: "#5C7D50", sageLight: "#E8F0E4", sageBg: "#F3F7F1",
  gold: "#C4A265", goldLight: "#F5EFE0",
  border: "#E8E2D8", borderLight: "#F0EBE3",
};
const font = { display: "'Merriweather', Georgia, serif", body: "'Inter', 'Helvetica Neue', sans-serif" };

/* ── NAV ── */
function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  useEffect(() => { const fn = () => setScrolled(window.scrollY > 50); window.addEventListener("scroll", fn); return () => window.removeEventListener("scroll", fn); }, []);
  return (
    <nav style={{ position: "fixed", top: "var(--banner-h, 0px)", left: 0, right: 0, zIndex: 100, background: scrolled ? "rgba(253,251,247,0.95)" : "transparent", backdropFilter: scrolled ? "blur(16px)" : "none", borderBottom: scrolled ? `1px solid ${C.borderLight}` : "1px solid transparent", transition: "all 0.4s ease", padding: "0 clamp(20px, 5vw, 72px)" }}>
      <div style={{ maxWidth: 1140, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", height: scrolled ? 110 : 132, transition: "height 0.4s ease" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Link to="/" style={{ textDecoration: "none", lineHeight: 0 }}>
            <BrandLogo iconSize={114} textSize={24} textColor={C.sageDark} />
          </Link>
          <span className="nav-tagline" style={{ fontSize: 10, color: C.textFaint, letterSpacing: "0.18em", textTransform: "uppercase", fontFamily: font.body }}>{TAGLINE}</span>
        </div>
        <div className="nav-links" style={{ display: "flex", alignItems: "center", gap: 36 }}>
          {[
            { name: "Home", path: "/" },
            { name: "For Parents", path: "/parents" },
            { name: "Programs", path: "/programs" }
          ].map(item => (
            <Link key={item.name} to={item.path} style={{ color: item.name === "For Parents" ? C.text : C.textMed, fontWeight: item.name === "For Parents" ? 600 : 400, fontSize: 14, textDecoration: "none", fontFamily: font.body, transition: "color 0.3s" }}
              onMouseEnter={e => e.target.style.color = C.text} onMouseLeave={e => { if (item.name !== "For Parents") e.target.style.color = C.textMed; }}
            >{item.name}</Link>
          ))}
          <a href="/#consultation" className="nav-cta" style={{ color: "#fff", background: C.sage, padding: "10px 26px", fontSize: 13, fontFamily: font.body, fontWeight: 600, textDecoration: "none", borderRadius: 6, transition: "all 0.3s" }}
            onMouseEnter={e => e.target.style.background = C.sageDark} onMouseLeave={e => e.target.style.background = C.sage}
          >Register Your Interest</a>
        </div>
        <button
          className="nav-mobile-toggle"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          style={{ display: "none" }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={C.text} strokeWidth="2" strokeLinecap="round">
            {mobileMenuOpen ? (
              <>
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </>
            ) : (
              <>
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </>
            )}
          </svg>
        </button>
      </div>
      {mobileMenuOpen && (
        <div className="nav-mobile-menu" style={{
          position: "absolute", top: "100%", left: 0, right: 0,
          background: "rgba(253,251,247,0.98)", backdropFilter: "blur(16px)",
          borderBottom: `1px solid ${C.borderLight}`, padding: "20px",
        }}>
          {[
            { name: "Home", path: "/" },
            { name: "For Parents", path: "/parents" },
            { name: "Programs", path: "/programs" }
          ].map(item => (
            <Link key={item.name} to={item.path} style={{
              display: "block", color: C.textMed, fontSize: 16, textDecoration: "none",
              fontFamily: font.body, fontWeight: 400, padding: "12px 0",
              borderBottom: `1px solid ${C.borderLight}`,
            }}
              onClick={() => setMobileMenuOpen(false)}
            >{item.name}</Link>
          ))}
          <a href="/#consultation" style={{
            display: "block", color: "#fff", background: C.sage, padding: "14px 26px",
            fontSize: 15, fontFamily: font.body, fontWeight: 600, textAlign: "center",
            textDecoration: "none", borderRadius: 6, marginTop: "16px",
          }}
            onClick={() => setMobileMenuOpen(false)}
          >Register Your Interest</a>
        </div>
      )}
    </nav>
  );
}

/* ── PAGE HERO ── */
function PageHero() {
  return (
    <section className="hero-section" style={{ padding: "140px clamp(20px,5vw,72px) 80px", background: `linear-gradient(170deg, ${C.bg}, ${C.bgWarm})`, position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", top: -80, right: -60, width: 400, height: 400, borderRadius: "50%", background: `radial-gradient(circle, ${C.sageLight} 0%, transparent 70%)`, opacity: 0.4 }} />
      <div className="grid-hero" style={{ maxWidth: 1140, margin: "0 auto", position: "relative", display: "grid", gridTemplateColumns: "1fr 480px", gap: 64, alignItems: "center" }}>
        <div>
          <FadeIn>
            <div style={{ fontSize: 12, color: C.sage, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 16, fontFamily: font.body, fontWeight: 600 }}>For intended parents</div>
          </FadeIn>
          <FadeIn delay={0.1}>
            <h1 className="responsive-h1 parents-hero-title" style={{ fontFamily: font.display, fontSize: "clamp(34px, 4.5vw, 56px)", color: C.text, fontWeight: 600, lineHeight: 1.12, maxWidth: 680, margin: "0 0 24px", letterSpacing: "-0.02em" }}>
              Everything you need to know before you decide
            </h1>
          </FadeIn>
          <FadeIn delay={0.2}>
            <p className="responsive-text-lg" style={{ fontSize: 18, color: C.textMed, maxWidth: 580, lineHeight: 1.75, fontFamily: font.body, margin: 0 }}>
              We know you have questions about legality, how it works, and whether this is right for your family. This page answers them honestly with no pressure, just clarity.
            </p>
          </FadeIn>
        </div>

        <FadeIn delay={0.25}>
          <div className="hero-image-wrapper" style={{ position: "relative" }}>
            <div style={{
              position: "absolute", top: -12, right: -12, width: "100%", height: "100%",
              borderRadius: 24, border: `2px solid ${C.sageLight}`, zIndex: 0,
            }} />
            <div style={{
              position: "relative", zIndex: 1, borderRadius: 20, overflow: "hidden",
              boxShadow: "0 8px 48px rgba(44,37,32,0.1)",
              aspectRatio: "4/3",
            }}>
              <img
                src="https://images.unsplash.com/photo-1555252333-9f8e92e65df9?w=960&h=720&fit=crop&q=80"
                alt="Expectant parents"
                style={{
                  width: "100%", height: "100%", objectFit: "cover", display: "block",
                }}
              />
              <div style={{
                position: "absolute", bottom: 0, left: 0, right: 0, height: "30%",
                background: `linear-gradient(to top, ${C.bgWarm}44, transparent)`,
              }} />
            </div>
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

/* ── LEGAL FRAMEWORK ── */
function LegalFramework() {
  return (
    <section className="section-padding-lg" style={{ padding: "100px clamp(20px,5vw,72px)", background: "#fff" }}>
      <div style={{ maxWidth: 1140, margin: "0 auto" }}>
        <FadeIn>
          <div style={{ fontSize: 12, color: C.sage, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 14, fontFamily: font.body, fontWeight: 600 }}>Legal framework</div>
        </FadeIn>
        <FadeIn delay={0.08}>
          <h2 className="responsive-h2" style={{ fontFamily: font.display, fontSize: "clamp(28px, 3.5vw, 42px)", color: C.text, fontWeight: 600, lineHeight: 1.2, margin: "0 0 20px" }}>
            This is not a grey area — it is law.
          </h2>
        </FadeIn>
        <FadeIn delay={0.14}>
          <p className="responsive-description" style={{ fontSize: 17, color: C.textMed, lineHeight: 1.75, maxWidth: 640, margin: "0 0 56px", fontFamily: font.body }}>
            In 2023, the UAE enacted Federal Decree-Law No. 17, creating the first regulated gestational surrogacy framework in the Gulf. Abu Dhabi's Department of Health now licenses and oversees every aspect of the process — from clinic accreditation to birth registration.
          </p>
        </FadeIn>

        <div className="grid-3-cols legal-framework-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 20 }}>
          {[
            { title: "Who is eligible", items: ["Legally married heterosexual couples", "Documented medical necessity for surrogacy", "Approval from DoH Medical Committee", "Ages 18–45 (intended mother)", "Own genetic material (own egg and sperm)"] },
            { title: "What the law guarantees", items: ["Only gestational surrogacy — carrier has zero genetic connection to your child", "Notarized tripartite agreement before any medical procedure", "Your names on the birth certificate from the moment of birth", "Carrier waives all parental rights in binding legal agreement", "All procedures at DoH-accredited facilities"] },
            { title: "What you should know", items: ["Egg donation is not currently permitted within the UAE for surrogacy cases", "All services performed in Abu Dhabi", "Mandatory insurance coverage for the carrier", "Both parents must provide spousal consent", "The process requires DoH eligibility certification"] },
          ].map((col, i) => (
            <FadeIn key={i} delay={0.1 + i * 0.08}>
              <div className="legal-framework-card" style={{ padding: 32, background: C.bg, borderRadius: 12, border: `1px solid ${C.borderLight}`, height: "100%" }}>
                <div style={{ fontSize: 16, color: C.text, fontFamily: font.body, fontWeight: 600, marginBottom: 20 }}>{col.title}</div>
                {col.items.map((item, j) => (
                  <div key={j} style={{ display: "flex", gap: 10, marginBottom: 14, alignItems: "start" }}>
                    <div style={{ width: 6, height: 6, borderRadius: "50%", background: C.sage, marginTop: 7, flexShrink: 0 }} />
                    <span style={{ fontSize: 14, color: C.textMed, lineHeight: 1.6, fontFamily: font.body }}>{item}</span>
                  </div>
                ))}
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── GESTATIONAL VS TRADITIONAL ── */
function GestationalExplainer() {
  return (
    <section className="section-padding-lg" style={{ padding: "100px clamp(20px,5vw,72px)", background: C.bgWarm }}>
      <div style={{ maxWidth: 880, margin: "0 auto" }}>
        <FadeIn>
          <div style={{ fontSize: 12, color: C.sage, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 14, fontFamily: font.body, fontWeight: 600 }}>Understanding the distinction</div>
        </FadeIn>
        <FadeIn delay={0.08}>
          <h2 className="responsive-h2" style={{ fontFamily: font.display, fontSize: "clamp(26px, 3vw, 38px)", color: C.text, fontWeight: 600, lineHeight: 1.25, margin: "0 0 20px" }}>
            Gestational surrogacy: your embryo, your child
          </h2>
        </FadeIn>
        <FadeIn delay={0.14}>
          <p className="responsive-text-lg" style={{ fontSize: 17, color: C.textMed, lineHeight: 1.8, margin: "0 0 48px", fontFamily: font.body }}>
            Under UAE law, only gestational surrogacy is permitted. This means the embryo is created using your own egg and sperm — the carrier contributes no genetic material and solely carries your child to term. The child is yours genetically, legally, and on every document from birth.
          </p>
        </FadeIn>

        <div className="grid-2-cols" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
          <FadeIn delay={0.2}>
            <div style={{ padding: 36, background: "#fff", borderRadius: 14, border: `2px solid ${C.sage}` }}>
              <div style={{ display: "inline-block", padding: "4px 14px", background: C.sageBg, borderRadius: 100, fontSize: 12, color: C.sageDark, fontFamily: font.body, fontWeight: 600, marginBottom: 16 }}>Permitted in the UAE</div>
              <h3 style={{ fontSize: 20, color: C.text, fontFamily: font.display, fontWeight: 600, margin: "0 0 12px" }}>Gestational surrogacy</h3>
              <p style={{ fontSize: 15, color: C.textMed, lineHeight: 1.7, fontFamily: font.body, margin: 0 }}>
                The embryo is created from the intended parents' own egg and sperm via IVF, with the carrier having no genetic relationship to the child. Parental rights belong entirely to you — this is the only form of surrogacy we facilitate.
              </p>
            </div>
          </FadeIn>
          <FadeIn delay={0.28}>
            <div style={{ padding: 36, background: "#fff", borderRadius: 14, border: `1px solid ${C.borderLight}`, opacity: 0.65 }}>
              <div style={{ display: "inline-block", padding: "4px 14px", background: "#FEF2F2", borderRadius: 100, fontSize: 12, color: "#991B1B", fontFamily: font.body, fontWeight: 600, marginBottom: 16 }}>Not permitted</div>
              <h3 style={{ fontSize: 20, color: C.text, fontFamily: font.display, fontWeight: 600, margin: "0 0 12px" }}>Traditional surrogacy</h3>
              <p style={{ fontSize: 15, color: C.textMed, lineHeight: 1.7, fontFamily: font.body, margin: 0 }}>
                The carrier's own egg is used, making her the genetic mother and creating complex legal and ethical issues. It is prohibited under UAE law and is not practiced in our programs anywhere.
              </p>
            </div>
          </FadeIn>
        </div>

        <FadeIn delay={0.36}>
          <div style={{ marginTop: 48, padding: 32, background: C.sageBg, borderRadius: 12, border: `1px solid ${C.sageLight}` }}>
            <div style={{ fontSize: 15, color: C.sageDark, fontFamily: font.body, fontWeight: 600, marginBottom: 8 }}>A note on Islamic perspectives</div>
            <p style={{ fontSize: 14, color: C.textMed, lineHeight: 1.7, fontFamily: font.body, margin: 0 }}>
              Multiple Islamic scholars have distinguished gestational surrogacy — where the child is genetically the intended parents' own — from forms of surrogacy that involve genetic mixing. The UAE's legal framework permits only the gestational form, using the married couple's own gametes, which aligns with the principle of preserving lineage (nasab). We encourage families with questions to consult a trusted scholar, and we are preparing a detailed resource on this topic with qualified religious guidance.
            </p>
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

/* ── PRIVACY ── */
function PrivacySection() {
  return (
    <section className="section-padding-lg" style={{ padding: "100px clamp(20px,5vw,72px)", background: "#fff" }}>
      <div className="grid-2-cols" style={{ maxWidth: 1140, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 64, alignItems: "start" }}>
        <div>
          <FadeIn>
            <div style={{ fontSize: 12, color: C.sage, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 14, fontFamily: font.body, fontWeight: 600 }}>Privacy & discretion</div>
          </FadeIn>
          <FadeIn delay={0.08}>
            <h2 className="responsive-h2" style={{ fontFamily: font.display, fontSize: "clamp(26px, 3vw, 38px)", color: C.text, fontWeight: 600, lineHeight: 1.25, margin: "0 0 20px" }}>
              Your decision, your privacy — a non-negotiable.
            </h2>
          </FadeIn>
          <FadeIn delay={0.14}>
            <p className="responsive-description" style={{ fontSize: 16, color: C.textMed, lineHeight: 1.75, fontFamily: font.body, margin: "0 0 20px" }}>
              We understand that for many families, the decision to pursue surrogacy is deeply personal. Some intended parents choose to share their journey openly while others prefer complete privacy — both are normal, and our process is built to support either from the very first conversation.
            </p>
          </FadeIn>
          <FadeIn delay={0.2}>
            <p style={{ fontSize: 16, color: C.textMed, lineHeight: 1.75, fontFamily: font.body, margin: 0 }}>
              Every interaction with us, from the first consultation to the final birth certificate, is held to the same standard of confidentiality you would expect from a private medical provider. Who you tell, and when, will always be your decision.
            </p>
          </FadeIn>
        </div>

        <FadeIn delay={0.15}>
          <div style={{ padding: 36, background: C.bg, borderRadius: 14, border: `1px solid ${C.borderLight}` }}>
            <div style={{ fontSize: 15, color: C.text, fontFamily: font.body, fontWeight: 600, marginBottom: 24 }}>Our confidentiality commitments</div>
            {[
              "Initial consultations are private — advisor-to-couple only, not recorded",
              "All medical records held in compliance with UAE data protection law",
              "No information shared with any third party without your written consent",
              "Documents handled through secure, private channels",
              "Your employer, insurance provider, and extended family are never contacted",
              "Staff bound by strict non-disclosure agreements",
            ].map((item, i) => (
              <div key={i} style={{ display: "flex", gap: 12, padding: "12px 0", borderBottom: i < 7 ? `1px solid ${C.borderLight}` : "none", alignItems: "start" }}>
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" style={{ flexShrink: 0, marginTop: 1 }}>
                  <rect x="1" y="1" width="16" height="16" rx="4" stroke={C.sage} strokeWidth="1.5" fill={C.sageBg} />
                  <path d="M6 9L8 11L12 7" stroke={C.sage} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span style={{ fontSize: 14, color: C.textMed, lineHeight: 1.55, fontFamily: font.body }}>{item}</span>
              </div>
            ))}
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

/* ── YOUR CARRIER ── */
function CarrierSection() {
  return (
    <section className="section-padding-lg" style={{ padding: "100px clamp(20px,5vw,72px)", background: C.bg }}>
      <div style={{ maxWidth: 1140, margin: "0 auto" }}>
        <FadeIn>
          <div style={{ fontSize: 12, color: C.sage, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 14, fontFamily: font.body, fontWeight: 600 }}>Your carrier</div>
        </FadeIn>
        <FadeIn delay={0.08}>
          <h2 className="responsive-h2" style={{ fontFamily: font.display, fontSize: "clamp(26px, 3vw, 38px)", color: C.text, fontWeight: 600, lineHeight: 1.25, margin: "0 0 20px" }}>
            Who carries your child and how we find her
          </h2>
        </FadeIn>
        <FadeIn delay={0.14}>
          <p className="responsive-text-lg" style={{ fontSize: 17, color: C.textMed, lineHeight: 1.75, maxWidth: 640, margin: "0 0 56px", fontFamily: font.body }}>
            Every carrier who enters our program must have chosen this path voluntarily, passed thorough medical and psychological screening, and met Abu Dhabi Department of Health standards. Each one joins a regulated medical program in the UAE as a verified, fully informed participant.
          </p>
        </FadeIn>

        <div className="grid-2-cols" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
          <FadeIn delay={0.2}>
            <div style={{ padding: 36, background: "#fff", borderRadius: 14, border: `1px solid ${C.borderLight}` }}>
              <div style={{ fontSize: 16, color: C.text, fontFamily: font.body, fontWeight: 600, marginBottom: 20 }}>Screening process</div>
              {[
                { step: "1", title: "Local recruitment", desc: "Dedicated recruiters in each country identify candidates and explain the program, requirements, and commitment involved." },
                { step: "2", title: "Medical screening", desc: "Full medical evaluation at an accredited local clinic: blood panel, infectious disease tests, reproductive ultrasound, hormonal assessment." },
                { step: "3", title: "Psychological evaluation", desc: "Licensed psychologist confirms informed consent, emotional readiness, and motivation, with background check completed." },
                { step: "4", title: "Profile creation", desc: "Professional profile with photos, video interview, personal bio, and medical summary is uploaded to our secure portal." },
                { step: "5", title: "Final screening in Abu Dhabi", desc: "After matching, full re-screening at the IVF center in Abu Dhabi ensures only carriers who pass both rounds proceed." },
              ].map((item, i) => (
                <div key={i} style={{ display: "flex", gap: 16, padding: "16px 0", borderBottom: i < 4 ? `1px solid ${C.borderLight}` : "none", alignItems: "start" }}>
                  <div style={{ width: 28, height: 28, borderRadius: 8, background: C.sageBg, border: `1px solid ${C.sageLight}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 12, color: C.sageDark, fontFamily: font.body, fontWeight: 600 }}>{item.step}</div>
                  <div>
                    <div style={{ fontSize: 14, color: C.text, fontFamily: font.body, fontWeight: 600, marginBottom: 4 }}>{item.title}</div>
                    <div style={{ fontSize: 13, color: C.textLight, lineHeight: 1.6, fontFamily: font.body }}>{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </FadeIn>

          <FadeIn delay={0.28}>
            <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
              <div style={{ padding: 36, background: "#fff", borderRadius: 14, border: `1px solid ${C.borderLight}` }}>
                <div style={{ fontSize: 16, color: C.text, fontFamily: font.body, fontWeight: 600, marginBottom: 20 }}>DoH eligibility criteria</div>
                {[
                  { label: "Age", value: "21 – 45" }, { label: "BMI", value: "19 – 30" },
                  { label: "Prior births", value: "At least one" }, { label: "Maximum births", value: "Four" },
                  { label: "Maximum C-sections", value: "Two" }, { label: "Chronic conditions", value: "None" },
                  { label: "Psychological evaluation", value: "Required" }, { label: "Background check", value: "Required" },
                ].map((item, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "11px 0", borderBottom: i < 7 ? `1px solid ${C.borderLight}` : "none" }}>
                    <span style={{ fontSize: 14, color: C.textLight, fontFamily: font.body }}>{item.label}</span>
                    <span style={{ fontSize: 14, color: C.text, fontFamily: font.body, fontWeight: 600 }}>{item.value}</span>
                  </div>
                ))}
              </div>

              <div style={{ padding: 36, background: "#fff", borderRadius: 14, border: `1px solid ${C.borderLight}` }}>
                <div style={{ fontSize: 16, color: C.text, fontFamily: font.body, fontWeight: 600, marginBottom: 12 }}>Carrier welfare</div>
                <p style={{ fontSize: 14, color: C.textMed, lineHeight: 1.7, fontFamily: font.body, margin: 0 }}>
                  Every carrier in our White Glove program receives agency-managed housing, full medical insurance, comprehensive prenatal care at the IVF center, psychological support throughout the pregnancy, and a structured recovery period after delivery. This is a regulated medical program and the carrier's wellbeing is both a legal requirement and a core value of this agency.
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
      </div>
    </section>
  );
}

/* ── AFTER BIRTH ── */
function AfterBirth() {
  return (
    <section style={{ padding: "100px clamp(20px,5vw,72px)", background: C.bgWarm }}>
      <div style={{ maxWidth: 880, margin: "0 auto" }}>
        <FadeIn>
          <div style={{ fontSize: 12, color: C.sage, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 14, fontFamily: font.body, fontWeight: 600 }}>After birth</div>
        </FadeIn>
        <FadeIn delay={0.08}>
          <h2 style={{ fontFamily: font.display, fontSize: "clamp(26px, 3vw, 38px)", color: C.text, fontWeight: 600, lineHeight: 1.25, margin: "0 0 20px" }}>
            What happens in the first 60 days
          </h2>
        </FadeIn>
        <FadeIn delay={0.14}>
          <p style={{ fontSize: 17, color: C.textMed, lineHeight: 1.75, margin: "0 0 48px", fontFamily: font.body }}>
            The journey doesn't end at delivery. Here's what to expect in the weeks after your child is born in Abu Dhabi.
          </p>
        </FadeIn>

        {[
          { title: "Birth and hospital discharge", time: "Day 1–3", desc: "Your child is born at a DoH-licensed hospital where you are present, and the hospital issues the birth notification with your names as parents. Typical discharge is 1–5 days after birth depending on whether a C-Section was performed." },
          { title: "Birth certificate registration", time: "Week 1–2", desc: "The birth notification is submitted to Abu Dhabi civil registration, and a UAE birth certificate is issued in your names. You are listed as the biological parents from the outset." },
          { title: "Carrier recovery and departure", time: "Week 2–4", desc: "The carrier enters a structured recovery period. In the White Glove program, we manage her post-delivery care, recovery housing, and return travel to her home country." },
          { title: "Passport and nationality", time: "Week 2–6", desc: "For UAE nationals, nationality registration follows standard procedures. For expat parents, you will apply for your child's passport through your home country's embassy or consulate in Abu Dhabi. As processing times vary by country, we advise beginning this process as early as possible." },
          { title: "DNA confirmation (if required)", time: "Week 1–4", desc: "Some embassies require DNA testing to confirm parentage before issuing a passport. This is a standard, non-invasive procedure, and we can help coordinate the testing and necessary documentation." },
          { title: "Travel home", time: "Week 4–8", desc: "Once your child's passport is issued, you are free to travel. For parents returning to countries outside the UAE, we recommend confirming any additional documentation requirements with your embassy in advance." },
        ].map((item, i) => (
          <FadeIn key={i} delay={0.06 * i}>
            <div style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: 24, padding: "28px 0", borderTop: `1px solid ${C.border}`, alignItems: "start" }}>
              <div style={{ fontSize: 13, color: C.sage, fontFamily: font.body, fontWeight: 600, paddingTop: 2 }}>{item.time}</div>
              <div>
                <div style={{ fontSize: 16, color: C.text, fontFamily: font.body, fontWeight: 600, marginBottom: 6 }}>{item.title}</div>
                <div style={{ fontSize: 15, color: C.textMed, lineHeight: 1.7, fontFamily: font.body }}>{item.desc}</div>
              </div>
            </div>
          </FadeIn>
        ))}
      </div>
    </section>
  );
}

/* ── FAQ ── */
function FAQ() {
  const [openIndex, setOpenIndex] = useState(null);
  const faqs = [
    { q: "Is surrogacy legal in the UAE?", a: "Yes. Federal Decree-Law No. 17 of 2023 established a legal framework for gestational surrogacy, with Abu Dhabi's Department of Health regulating and licensing the process. All procedures take place at DoH-accredited facilities." },
    { q: "Who is eligible for the program?", a: "Legally married heterosexual couples with documented medical necessity for surrogacy, where both partners are between 18 and 45 and use their own genetic material (egg and sperm). The DoH Medical Committee reviews each case and issues an eligibility certificate." },
    { q: "Does the carrier have any genetic connection to my child?", a: "None whatsoever. In gestational surrogacy, the embryo is created from your egg and your partner's sperm, with the carrier's body carrying the pregnancy but contributing no DNA—your child is genetically yours." },
    { q: "Does the carrier have any parental rights?", a: "No. A notarized tripartite agreement is signed before embryo transfer in which the carrier waives all parental rights, and under UAE law, you are listed as the parents on the birth certificate from the moment of birth." },
    { q: "Is egg donation available in the UAE?", a: "Currently, third-party egg donation is not permitted within the UAE for surrogacy cases. If you require donor eggs, the IVF and embryo creation can take place at an accredited international facility, and the resulting embryo can be transferred to Abu Dhabi for the surrogacy. We can advise on the best pathway for your situation." },
    { q: "How much does it cost?", a: "We offer two service tiers with different levels of coordination and support. Medical care is contracted separately and directly with the IVF center. We provide a detailed, transparent breakdown of all program components during your consultation." },
    { q: "How long does the process take?", a: "From application to birth, typically 12–18 months. Initial qualification and matching takes 4–10 weeks, with carrier screening, visa, and travel adding another 6–10 weeks, followed by the approximately 9-month pregnancy. We provide a detailed timeline specific to your situation at consultation." },
    { q: "Do I need to live in Abu Dhabi?", a: "No. You will need to attend 2–3 key appointments in Abu Dhabi—the initial consultation and eligibility process, potentially a visit during embryo transfer, and of course for the birth. Our team handles coordination remotely between those milestones." },
    { q: "Where do carriers come from?", a: "We recruit from 13 countries globally, with each carrier pre-screened against Abu Dhabi DoH criteria in her home country before entering our database. All medical procedures take place in Abu Dhabi under UAE law—the carrier's home country is used only for recruitment and initial screening." },
    { q: "How do I choose a carrier?", a: "After you're approved, you access our secure carrier database where each profile includes a medical summary, video introduction, personal bio, and screening results. You browse, review, and select, then we arrange a video introduction between you and the carrier before confirming the match." },
    { q: "What if the IVF transfer fails?", a: "Transfer success rates vary — typically 50–70% per cycle with a PGT-A tested embryo. If a transfer does not result in pregnancy, subsequent cycles can be arranged. We recommend discussing success rate expectations and contingency planning during your consultation." },
    { q: "What happens to my child's citizenship?", a: "For UAE nationals, nationality registration follows standard UAE procedures. For expat parents, your child's citizenship follows your nationality and you apply through your embassy in Abu Dhabi. Some embassies require DNA confirmation before issuing a passport, and we help coordinate all the documentation." },
    { q: "How private is this process?", a: "Completely. Your consultation is confidential, your medical records are protected under UAE data privacy law, and no information is shared with third parties without your written consent. Your employer, insurance, and extended family are never contacted, and we handle every communication with the same discretion expected of a private medical provider." },
    { q: "Can I use this program if I am not a UAE resident?", a: "Yes. The program is available to eligible married couples regardless of nationality or residency, subject to DoH approval. You will need to travel to Abu Dhabi for key steps, with your child's passport issued through your home country's embassy." },
    { q: "What is the difference between your two service tiers?", a: "Matching (Tier 1) covers carrier sourcing, screening, visa coordination, and introduction to the IVF center. White Glove (Tier 2) adds full concierge management through birth: carrier housing, welfare monitoring, a dedicated coordinator, prenatal coordination, and post-birth support. In both tiers, medical is a separate contract between you and the IVF center." },
    { q: "Is the carrier treated ethically?", a: "Yes. Every carrier is a voluntary participant in a regulated medical program, receiving comprehensive medical care, psychological support, insurance coverage, and structured compensation. In our White Glove program, we manage her housing, welfare, and recovery, with UAE law governing her rights and protections." },
  ];

  return (
    <section style={{ padding: "100px clamp(20px,5vw,72px)", background: "#fff" }}>
      <div style={{ maxWidth: 820, margin: "0 auto" }}>
        <FadeIn>
          <div style={{ fontSize: 12, color: C.sage, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 14, fontFamily: font.body, fontWeight: 600 }}>Frequently asked questions</div>
        </FadeIn>
        <FadeIn delay={0.08}>
          <h2 style={{ fontFamily: font.display, fontSize: "clamp(26px, 3vw, 38px)", color: C.text, fontWeight: 600, lineHeight: 1.25, margin: "0 0 48px" }}>
            Honest answers to the questions that matter
          </h2>
        </FadeIn>

        {faqs.map((faq, i) => (
          <FadeIn key={i} delay={Math.min(i * 0.03, 0.3)}>
            <div style={{ borderTop: `1px solid ${C.borderLight}` }}>
              <button
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                style={{
                  width: "100%", textAlign: "left", background: "none", border: "none",
                  padding: "20px 0", cursor: "pointer", display: "flex",
                  justifyContent: "space-between", alignItems: "center", gap: 16,
                }}
              >
                <span style={{ fontSize: 16, color: C.text, fontFamily: font.body, fontWeight: 500, lineHeight: 1.4 }}>{faq.q}</span>
                <span style={{
                  fontSize: 20, color: C.textFaint, flexShrink: 0, transition: "transform 0.3s",
                  transform: openIndex === i ? "rotate(45deg)" : "rotate(0deg)",
                  fontWeight: 300,
                }}>+</span>
              </button>
              <div style={{
                maxHeight: openIndex === i ? 400 : 0, overflow: "hidden",
                transition: "max-height 0.4s ease",
              }}>
                <p style={{ fontSize: 15, color: C.textMed, lineHeight: 1.7, fontFamily: font.body, margin: "0 0 20px", paddingRight: 40 }}>{faq.a}</p>
              </div>
            </div>
          </FadeIn>
        ))}
      </div>
    </section>
  );
}

/* ── CTA ── */
function CTASection() {
  return (
    <section id="consultation" className="section-padding" style={{ padding: "80px clamp(20px,5vw,72px)", background: C.bgSoft }}>
      <div style={{ maxWidth: 640, margin: "0 auto", textAlign: "center" }}>
        <FadeIn>
          <h2 className="responsive-h2" style={{ fontFamily: font.display, fontSize: "clamp(26px, 3vw, 36px)", color: C.text, fontWeight: 600, lineHeight: 1.3, margin: "0 0 16px" }}>
            Still have questions? That's exactly what the consultation is for.
          </h2>
        </FadeIn>
        <FadeIn delay={0.08}>
          <p className="responsive-description" style={{ fontSize: 16, color: C.textMed, lineHeight: 1.7, fontFamily: font.body, margin: "0 0 32px" }}>
            A private, 30-minute call with no commitment, where we listen to your situation and explain what the process looks like for you specifically.
          </p>
        </FadeIn>
        <FadeIn delay={0.16}>
          <div className="btn-group" style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap" }}>
            <a href="/#consultation" style={{ padding: "14px 32px", background: C.sage, color: "#fff", textDecoration: "none", fontSize: 15, fontFamily: font.body, fontWeight: 600, borderRadius: 6, transition: "all 0.3s" }}
              onMouseEnter={e => e.target.style.background = C.sageDark} onMouseLeave={e => e.target.style.background = C.sage}
            >Register Your Interest</a>
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

/* ── FOOTER ── */
function Footer() {
  return (
    <footer style={{ padding: "56px clamp(20px,5vw,72px) 36px", background: C.text }}>
      <div style={{ maxWidth: 1000, margin: "0 auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", marginBottom: 44 }}>
          <div>
            <div style={{ fontFamily: font.display, fontSize: 20, color: "#fff", fontWeight: 600, marginBottom: 8 }}>{BRAND}</div>
            <div style={{ fontSize: 13, color: "rgba(255,255,255,0.4)", fontFamily: font.body, lineHeight: 1.6, maxWidth: 260 }}>Surrogacy coordination agency.<br />Abu Dhabi, United Arab Emirates.</div>
          </div>
          <div style={{ display: "flex", gap: 48 }}>
            <div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 14, fontFamily: font.body, fontWeight: 600 }}>Navigate</div>
              {[
                { name: "Home", path: "/" },
                { name: "For Parents", path: "/parents" },
                { name: "Programs", path: "/programs" },
                { name: "FAQ", path: "#" }
              ].map(item => (
                item.path === "#" ? (
                  <button key={item.name} type="button" style={{ display: "block", color: "rgba(255,255,255,0.55)", fontSize: 14, textDecoration: "none", marginBottom: 10, fontFamily: font.body, background: "none", border: "none", padding: 0, cursor: "default" }}>{item.name}</button>
                ) : (
                  <Link key={item.name} to={item.path} style={{ display: "block", color: "rgba(255,255,255,0.55)", fontSize: 14, textDecoration: "none", marginBottom: 10, fontFamily: font.body }}>{item.name}</Link>
                )
              ))}
            </div>
            <div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 14, fontFamily: font.body, fontWeight: 600 }}>Contact</div>
              <div style={{ fontSize: 14, color: "rgba(255,255,255,0.55)", fontFamily: font.body }}>info@zayalife.ae</div>
            </div>
          </div>
        </div>
        <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 20, display: "flex", justifyContent: "space-between" }}>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.25)", fontFamily: font.body }}>© 2026 {BRAND}. All rights reserved.</div>
        </div>
      </div>
    </footer>
  );
}

export default function App() {
  useEffect(() => {
    // Handle hash navigation for smooth scrolling
    if (window.location.hash) {
      setTimeout(() => {
        const id = window.location.hash.substring(1);
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, []);

  return (
    <div style={{ background: C.bg, minHeight: "100vh", paddingTop: "var(--banner-h, 0px)" }}>
      <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;1,400;1,500;1,600&family=Source+Sans+3:wght@300;400;500;600&display=swap" rel="stylesheet" />
      <Nav />
      <PageHero />
      <LegalFramework />
      <GestationalExplainer />
      <PrivacySection />
      <CarrierSection />
      <AfterBirth />
      <FAQ />
      <CTASection />
      <Footer />
    </div>
  );
}
